import { useState, useEffect } from 'react'
import { Users, Mail, UserPlus, Shield, Trash2, Crown, X, Clock } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useToast } from '../../hooks/useToast'
import { useConfirm } from '../../hooks/useConfirm'
import { getUserOrganizations, getOrganizationMembers, addOrganizationMember, updateMemberRole, removeOrganizationMember, getOrganizationInvitations, cancelInvitation } from '../../services/organizationService'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'

const roleColors = {
  owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  member: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
}

const roleIcons = {
  owner: Crown,
  admin: Shield,
  member: Users,
  viewer: Users
}

export default function Team() {
  const { user } = useAuthStore()
  const { success: showSuccess, error: showError, toastState, closeToast } = useToast()
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm()
  const [organizations, setOrganizations] = useState([])
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [members, setMembers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingInvitations, setLoadingInvitations] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [invite, setInvite] = useState({
    email: '',
    role: 'member'
  })

  useEffect(() => {
    loadData()
  }, [user])

  useEffect(() => {
    // Reload data when page gains focus (e.g., after accepting invitation)
    const handleFocus = () => {
      loadData()
    }
    
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user])

  useEffect(() => {
    if (selectedOrg) {
      loadMembers()
    }
  }, [selectedOrg])

  const loadData = async () => {
    if (!user) return
    
    try {
      const { data: orgs } = await getUserOrganizations(user.id)
      setOrganizations(orgs || [])
      
      if (orgs && orgs.length > 0) {
        setSelectedOrg(orgs[0].id)
      }
    } catch (error) {
      console.error('Error loading organizations:', error)
    }
  }

  const loadMembers = async () => {
    if (!selectedOrg) return
    
    try {
      setLoading(true)
      const { data, error } = await getOrganizationMembers(selectedOrg)
      
      if (error) throw error
      setMembers(data || [])
      
      // Load invitations
      await loadInvitations()
    } catch (error) {
      console.error('Error loading members:', error)
      showError('Failed to load team members')
    } finally {
      setLoading(false)
    }
  }

  const loadInvitations = async () => {
    if (!selectedOrg) return
    
    try {
      setLoadingInvitations(true)
      const { data, error } = await getOrganizationInvitations(selectedOrg)
      
      if (error) throw error
      setInvitations(data?.filter(inv => inv.status === 'pending') || [])
    } catch (error) {
      console.error('Error loading invitations:', error)
    } finally {
      setLoadingInvitations(false)
    }
  }

  const handleInvite = async (e) => {
    e.preventDefault()
    
    if (processing) return
    
    try {
      setProcessing(true)
      const { error } = await addOrganizationMember(selectedOrg, invite.email, invite.role)
      
      if (error) throw error
      
      showSuccess('Invitation sent successfully!')
      setShowInviteModal(false)
      setInvite({ email: '', role: 'member' })
      loadInvitations()
    } catch (error) {
      console.error('Error inviting member:', error)
      showError(error.message || 'Failed to send invitation')
    } finally {
      setProcessing(false)
    }
  }

  const handleCancelInvitation = async (invitationId) => {
    const confirmed = await confirm({
      title: 'Cancel Invitation',
      message: 'Are you sure you want to cancel this invitation?',
      confirmText: 'Cancel Invitation',
      cancelText: 'Keep'
    })
    
    if (!confirmed) return
    
    try {
      const { error } = await cancelInvitation(invitationId)
      
      if (error) throw error
      
      showSuccess('Invitation cancelled')
      loadInvitations()
    } catch (error) {
      console.error('Error cancelling invitation:', error)
      showError('Failed to cancel invitation')
    }
  }

  const handleRemoveMember = async (memberId, memberName) => {
    const confirmed = await confirm({
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${memberName} from the team?`,
      confirmText: 'Remove',
      cancelText: 'Cancel'
    })
    
    if (!confirmed) return
    
    try {
      const { error } = await removeOrganizationMember(memberId)
      
      if (error) throw error
      
      showSuccess('Member removed successfully')
      loadMembers()
    } catch (error) {
      console.error('Error removing member:', error)
      showError('Failed to remove member')
    }
  }

  const handleChangeRole = async (memberId, newRole, memberName) => {
    const confirmed = await confirm({
      title: 'Change Member Role',
      message: `Change ${memberName}'s role to ${newRole}?`,
      confirmText: 'Change Role',
      cancelText: 'Cancel'
    })
    
    if (!confirmed) return
    
    try {
      const { error } = await updateMemberRole(memberId, newRole)
      
      if (error) throw error
      
      showSuccess('Role updated successfully')
      loadMembers()
    } catch (error) {
      console.error('Error updating role:', error)
      showError('Failed to update role')
    }
  }

  const currentUserMember = members.find(m => m.user?.id === user?.id)
  const canInvite = currentUserMember && ['owner', 'admin'].includes(currentUserMember.role)
  const canManage = (memberRole) => {
    if (!currentUserMember) return false
    if (currentUserMember.role === 'owner') return true
    if (currentUserMember.role === 'admin' && memberRole !== 'owner') return true
    return false
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Team</h1>
          <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mt-1 sm:mt-2">
            Manage team members and roles
          </p>
        </div>
        <div className="relative group w-full sm:w-auto">
          <button
            onClick={() => {
              if (!canInvite) {
                showError('Only owners and admins can invite team members')
                return
              }
              setShowInviteModal(true)
            }}
            disabled={!canInvite}
            className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base w-full transition-all ${
              canInvite 
                ? 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90 cursor-pointer' 
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
            title={!canInvite ? 'Only owners and admins can invite team members' : ''}
          >
            <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
            Invite Member
          </button>
          {!canInvite && currentUserMember && (
            <div className="absolute -bottom-8 left-0 right-0 text-xs text-center text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Only admins/owners can invite
            </div>
          )}
        </div>
      </div>

      {/* Organization Selector */}
      <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6 mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium mb-2">Organization</label>
        <select
          value={selectedOrg || ''}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="w-full max-w-md px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
        >
          {organizations.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>

      {/* Pending Invitations */}
      {canInvite && invitations.length > 0 && (
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="font-semibold text-lg">Pending Invitations</h2>
            <span className="ml-auto text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-2 py-1 rounded-full">
              {invitations.length}
            </span>
          </div>
          <div className="space-y-2">
            {invitations.map(invitation => (
              <div 
                key={invitation.id} 
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{invitation.email}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-light dark:text-muted-dark">
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full">
                      {invitation.role}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleCancelInvitation(invitation.id)}
                  className="ml-3 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  title="Cancel invitation"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 sm:py-20">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      ) : members.length === 0 ? (
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-8 sm:p-12 text-center">
          <Users className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold mb-2">No Team Members</h3>
          <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mb-6">
            Start by inviting new members to your team
          </p>
          <button
            onClick={() => {
              if (!canInvite) {
                showError('Only owners and admins can invite team members')
                return
              }
              setShowInviteModal(true)
            }}
            disabled={!canInvite}
            className={`px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold transition-all text-sm sm:text-base ${
              canInvite 
                ? 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90 cursor-pointer' 
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
            title={!canInvite ? 'Only owners and admins can invite team members' : ''}
          >
            Invite Member
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold">Member</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold">Email</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold">Role</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold">Joined</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {members.map(member => {
                const RoleIcon = roleIcons[member.role] || Users
                
                return (
                  <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        {/* Avatar: initials always visible as bg, image on top */}
                        <div className="relative w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden shrink-0">
                          <div className="absolute inset-0 bg-gradient-to-br from-accent to-purple-500 flex items-center justify-center text-white font-semibold text-xs sm:text-sm select-none">
                            {(member.user?.full_name?.[0] || member.user?.email?.[0] || '?').toUpperCase()}
                          </div>
                          {member.user?.avatar_url && (
                            <img
                              src={member.user.avatar_url}
                              alt={member.user?.full_name || 'User'}
                              className="absolute inset-0 w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm sm:text-base truncate">
                            {member.user?.full_name || 'User'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-muted-light dark:text-muted-dark">
                      <div className="max-w-[150px] sm:max-w-none truncate">{member.user?.email || 'No email'}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      {canManage(member.role) ? (
                        <select
                          value={member.role}
                          onChange={(e) => handleChangeRole(member.id, e.target.value, member.user?.full_name || member.user?.email)}
                          className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium border-0 outline-none ${roleColors[member.role]}`}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                          {currentUserMember?.role === 'owner' && <option value="owner">Owner</option>}
                        </select>
                      ) : (
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium inline-flex items-center gap-1 ${roleColors[member.role]}`}>
                          <RoleIcon className="w-3 h-3" />
                          {member.role === 'owner' && 'Owner'}
                          {member.role === 'admin' && 'Admin'}
                          {member.role === 'member' && 'Member'}
                          {member.role === 'viewer' && 'Viewer'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-muted-light dark:text-muted-dark whitespace-nowrap">
                      {new Date(member.created_at).toLocaleDateString('en-US')}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      {member.user?.id !== user?.id && (
                        <button
                          onClick={() => {
                            if (!canManage(member.role)) {
                              showError('You do not have permission to remove this member')
                              return
                            }
                            handleRemoveMember(member.id, member.user?.full_name || member.user?.email)
                          }}
                          disabled={!canManage(member.role)}
                          className={`transition-colors ${
                            canManage(member.role)
                              ? 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer'
                              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          }`}
                          title={!canManage(member.role) ? 'You do not have permission to remove this member' : 'Remove member'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl max-w-lg w-full p-5 sm:p-6 lg:p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Invite New Member</h2>
            
            <form onSubmit={handleInvite} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  required
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  placeholder="user@example.com"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Role *</label>
                <select
                  value={invite.role}
                  onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                >
                  <option value="viewer">Viewer - Can view projects only</option>
                  <option value="member">Member - Can add and edit projects</option>
                  <option value="admin">Admin - Can manage projects and members</option>
                  {currentUserMember?.role === 'owner' && (
                    <option value="owner">Owner - Full permissions</option>
                  )}
                </select>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:pt-4">
                <button
                  type="submit"
                  disabled={processing}
                  className="flex-1 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold hover:opacity-90 transition-opacity text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white dark:border-black border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  disabled={processing}
                  className="flex-1 py-2.5 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onConfirm={handleConfirm}
        onClose={handleCancel}
      />

      {/* Toast Notifications */}
      <Toast
        isOpen={toastState.isOpen}
        message={toastState.message}
        type={toastState.type}
        onClose={closeToast}
      />
    </div>
  )
}
